import React from 'react';
import { Button, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useMessage from 'src/app/hooks/useMessage';
import handleError from '@fuse/core/errorHandler';
import { useCreatePurposeMutation, useEditStatusMutation } from 'src/app/services/appointmentApi';

const defaultValues = {
  id: undefined,
  nameUz: '',
  nameRu: '',
};

const ALLOWED_PATTERN = /^[\p{L}\p{N}]/u;

const AppointmentPurposeForm = ({ appointmentStatus, isEdit, onClose, refetch }) => {
  const { t } = useTranslation('translations');
  const { showMessage } = useMessage();
  const [update, { isLoading }] = useEditStatusMutation();
  const [create, { isLoading: isCreateLoading }] = useCreatePurposeMutation();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
  } = useForm({
    mode: 'onChange',
    defaultValues: appointmentStatus ?? defaultValues,
  });

  const handleClose = () => {
    onClose?.();
    reset(defaultValues);
    refetch?.();
  };

  const trimObject = (obj) =>
    Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
    );

  const makeRules = () => ({
    required: t('EmptyInput') || true,
    validate: (value) => {
      const v = (value ?? '').trim();
      if (!v) return t('EmptyInput') || 'This field cannot be empty';
      if (!ALLOWED_PATTERN.test(v)) {
        return (
          t('SpecialCharactersNotAllowed') ||
          "Only letters, numbers, spaces and - . , _ ( ) / & : + ' are allowed"
        );
      }
      return true;
    },
  });

  const onChangeTrimStart = (fieldOnChange) => (e) => {
    fieldOnChange(e.target.value.trimStart());
  };

  const onSubmit = async (values) => {
    const payload = trimObject({
      id: values.id,
      nameUz: values.nameUz,
      nameRu: values.nameRu,
    });

    const message = isEdit ? t('DataSuccessfullyUpdated') : t('SuccessfullyAdded');
    const promise = isEdit ? update(payload) : create(payload);

    promise
      .unwrap()
      .then(() => {
        showMessage({ message, variant: 'success' });
        handleClose();
      })
      .catch((err) => handleError({ response: err }));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-10 pt-10 max-w-7xl">
      <Controller
        name="nameUz"
        control={control}
        rules={makeRules()}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label={`${t('Title')} (uz)`}
            error={!!errors.nameUz}
            helperText={errors.nameUz?.message}
            variant="outlined"
            required
            onChange={onChangeTrimStart(field.onChange)}
          />
        )}
      />
      <Controller
        name="nameRu"
        control={control}
        rules={makeRules()}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label={`${t('Title')} (рус)`}
            error={!!errors.nameRu}
            helperText={errors.nameRu?.message}
            variant="outlined"
            required
            onChange={onChangeTrimStart(field.onChange)}
          />
        )}
      />
      <Button
        disabled={isLoading || isCreateLoading || !isValid}
        type="submit"
        className="w-full"
        variant="contained"
        color="secondary"
      >
        <span className="mx-8">{t('Save')}</span>
      </Button>
    </form>
  );
};

export default AppointmentPurposeForm;
